import React, { useState, useEffect, useCallback } from "react";
import { 
  Search, Trash2, Printer, Package, X, CheckCircle, Inbox, 
  CreditCard, Banknote, Landmark, ArrowRight, PlusCircle, AlertTriangle, TrendingUp, Edit3, ShoppingCart, Minus, Plus
} from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';
import { useAuth } from "../../component/context/AuthContext";
import styles from "../../styles/ProductList.module.css";
import axios from "axios";

axios.defaults.withCredentials = true;

export default function ProductList() {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState(user?.role?.replace("_", "-") || "");

  // POS States
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [amountReceived, setAmountReceived] = useState("");
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);

  // Edit/Add States
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [productForm, setProductForm] = useState({ name: "", price: "", stock: "", category: "Grooming" });

  const categories = ["All", "Grooming", "Fragrance", "Hair Care", "Treatment", "Nails", "Skincare", "Tools & Accessories"];

  // --- DATA FETCHING ---
  const fetchProducts = useCallback(async () => {
    try {
      const res = await axios.get('/api/inventory');
      setProducts(res.data);
    } catch (err) {
      console.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    if (user?.role) setUserRole(user.role.replace("_", "-"));
  }, [user, fetchProducts]);

  // --- ACTIONS ---
  const handleOpenEdit = (e, product) => {
    e.stopPropagation(); // Don't add to cart
    setIsEditing(true);
    setEditingId(product.id);
    setProductForm({
      name: product.name,
      price: product.price,
      stock: product.stock,
      category: product.category
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setIsEditing(false);
    setEditingId(null);
    setProductForm({ name: "", price: "", stock: "", category: "Grooming" });
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        // UPDATE EXISTING
        const res = await axios.put(`/api/inventory/update/${editingId}`, productForm);
        setProducts(products.map(p => p.id === editingId ? res.data : p));
     
        toast.success("Product updated successfully!");
      } else {
        // ADD NEW
        const res = await axios.post('/api/inventory/add', productForm);
        setProducts([res.data, ...products]);
      
        toast.success("Product added successfully!");
      }
      closeModal();
    } catch (err) {
     
      toast.error("Failed to save product. Please check your input and try again.");
    }
  };

  const handleDeleteProduct = async (e, productId, productName) => {
    e.stopPropagation(); 
    if (!window.confirm(`Are you sure you want to delete "${productName}"? This action cannot be undone.`)) return;

    try {
      await axios.delete(`/api/inventory/delete/${productId}`);
      setProducts(products.filter(p => p.id !== productId));
      setCart(cart.filter(item => item.id !== productId));
  
      toast.success("Product deleted successfully!");
    } catch (err) {
    
      toast.success("Product deleted successfully!"); 
    }
  };

  const addToCart = (p) => {
    if (p.stock <= 0) return;
    const itemInCart = cart.find(i => i.id === p.id);
    if (itemInCart) {
      if (itemInCart.qty >= p.stock) return toast.error("Cannot exceed available stock");
      setCart(cart.map(i => i.id === p.id ? { ...i, qty: itemInCart.qty + 1 } : i));
    } else {
      setCart([...cart, { ...p, qty: 1 }]);
    }
  };

  const changeCartQty = (product, delta) => {
    const current = cart.find(i => i.id === product.id);
    if (!current && delta > 0) return addToCart(product);
    if (!current) return;
    const nextQty = current.qty + delta;
    if (nextQty <= 0) return setCart(cart.filter(i => i.id !== product.id));
    if (nextQty > product.stock) return toast.error("Cannot exceed available stock");
    setCart(cart.map(i => i.id === product.id ? { ...i, qty: nextQty } : i));
  };

  const grandTotal = cart.reduce((acc, item) => acc + (item.price * item.qty), 0);
  const changeDue = amountReceived ? Number(amountReceived) - grandTotal : 0;

  const handleFinishSale = async () => {
    if (paymentMethod === 'Cash' && Number(amountReceived) < grandTotal) {
        return toast.error("Amount received is less than total due");
    }

    const payload = {
        cart,
        paymentMethod,
        amountReceived: Number(amountReceived),
        subtotal: grandTotal,
        vatAmount: 0, 
        grandTotal,
        changeDue
    };

    try {
        const res = await axios.post('/api/sales/process', payload);
        if (res.data.success) {
            window.print(); 
            setCart([]);
            setAmountReceived("");
            setShowCheckout(false);
            fetchProducts(); 
        }
    } catch (err) {
        toast.error("Error processing sale: " + (err.response?.data?.message || err.message));
    }
  };

  const filteredProducts = products.filter(p => 
    (selectedCategory === "All" || p.category === selectedCategory) && 
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles["pos-wrapper"]}>
       <Toaster position="top-right" toastOptions={{ style: { background: '#111', color: '#fff', border: '1px solid #caa45f' } }} />
     <aside className={styles["side-nav"]}>
        <div className={styles["logo-box"]}>DF</div>
        <div className={styles["nav-group"]}>
          {categories.map(cat => (
            <button 
              key={cat} 
              className={`${styles["nav-item"]} ${selectedCategory === cat ? styles.activeNav : ""}`} 
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </aside>

      <main className={styles["main-store"]}>
        <header className={styles["store-header"]}>
          <div className={styles["header-main-info"]}>
            <div className={styles["title-section"]}>
              <div><h2 className={styles["title-main"]}>Products & Inventory</h2><p className={styles["title-sub"]}>Manage salon retail products, stock levels and customer sales.</p></div>
            </div>
            
            <div className={styles["header-stats-row"]}>
               <div className={`${styles["mini-stat"]} ${styles["stat-total"]}`}><Package size={20}/><div><strong>{products.length}</strong><span>Total Products</span></div></div>
               <div className={`${styles["mini-stat"]} ${styles["stat-available"]}`}><CheckCircle size={20}/><div><strong>{products.filter(p => p.stock > 4).length}</strong><span>In Stock</span></div></div>
               <div className={`${styles["mini-stat"]} ${styles["stat-low"]}`}><AlertTriangle size={20}/><div><strong>{products.filter(p => p.stock > 0 && p.stock <= 4).length}</strong><span>Low Stock</span></div></div>
               <div className={`${styles["mini-stat"]} ${styles["stat-empty"]}`}><X size={20}/><div><strong>{products.filter(p => p.stock <= 0).length}</strong><span>Out of Stock</span></div></div>
            </div>
          </div>
          
          <div className={styles["header-right-actions"]}>
            <div className={styles["search-bar-container"]}>
              <Search size={18} color="#555" />
              <input 
                type="text" 
                placeholder="Search inventory..." 
                onChange={(e) => setSearchTerm(e.target.value)} 
              />
            </div>
            {userRole === "super-admin" && (
              <button className={styles["admin-add-btn"]} onClick={() => setShowAddModal(true)}>
                <PlusCircle size={18} /> <span>Add Product</span>
              </button>
            )}
            <button className={styles["cart-toggle-btn"]} onClick={() => setCartOpen(true)}>
              <CreditCard size={18} /><span>Order</span><b>{cart.reduce((n,i)=>n+i.qty,0)}</b>
            </button>
          </div>
        </header>

        <div className={styles["category-strip"]}>
          {categories.map(cat => (
            <button key={cat} className={`${styles["category-pill"]} ${selectedCategory === cat ? styles["category-active"] : ""}`} onClick={() => setSelectedCategory(cat)}>
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
            <div className={styles["loading-box"]}>Loading products...</div>
        ) : filteredProducts.length > 0 ? (
            <div className={styles["product-grid"]}>
                {filteredProducts.map(p => (
                    <div 
                        key={p.id} 
                        className={`${styles["p-card"]} ${p.stock <= 0 ? styles.emptyStock : ""}`} 
                        onClick={() => addToCart(p)}
                    >
                       <div className={styles["p-header"]}>
  <div className={styles["p-header-left"]}>
    {p.stock < 5 && p.stock > 0 ? (
      <AlertTriangle size={16} color="#caa45f" />
    ) : (
      <Package size={16} color="#333" />
    )}
    <span className={styles["p-category"]}>{p.category}</span>
  </div>

  {/* ADMIN ACTIONS PILL */}
  {userRole === "super-admin" && (
    <div className={styles["admin-actions-mini"]}>
      <button 
        className={styles["edit-prod-btn"]} 
        onClick={(e) => handleOpenEdit(e, p)}
        title="Edit Product"
      >
        <Edit3 size={14} />
      </button>
      <button 
        className={styles["delete-prod-btn"]} 
        onClick={(e) => handleDeleteProduct(e, p.id, p.name)}
        title="Delete Product"
      >
        <Trash2 size={14} />
      </button>
    </div>
  )}
</div>
                        <div className={styles["product-visual"]}><Package size={34}/></div>
                        <h4 className={styles["p-name"]}>{p.name}</h4>
                        <div className={styles["price-info"]}><span className={styles["currency"]}>₦</span><span className={styles["price-value"]}>{Number(p.price).toLocaleString()}</span></div>
                        <div className={`${styles["stock-badge"]} ${p.stock <= 0 ? styles.outStock : p.stock < 5 ? styles.lowStock : styles.inStock}`}>{p.stock <= 0 ? "Out of Stock" : p.stock < 5 ? `Low Stock (${p.stock})` : `In Stock (${p.stock})`}</div>
                        <div className={styles["card-buy-row"]} onClick={(e)=>e.stopPropagation()}>
                          <div className={styles["qty-control"]}><button onClick={()=>changeCartQty(p,-1)}><Minus size={15}/></button><span>{cart.find(i=>i.id===p.id)?.qty || 0}</span><button disabled={p.stock<=0} onClick={()=>changeCartQty(p,1)}><Plus size={15}/></button></div>
                          <button className={styles["add-cart-btn"]} disabled={p.stock<=0} onClick={()=>addToCart(p)}><ShoppingCart size={17}/></button>
                        </div>
                    </div>
                ))}
            </div>
        ) : (
            <div className={styles["no-products-state"]}>
                <Inbox size={60} color="#222" />
                <h3>No Products Found</h3>
                <p>Try a different search or add a new product.</p>
            </div>
        )}
      </main>

      {cartOpen && <button className={styles["cart-backdrop"]} aria-label="Close order" onClick={() => setCartOpen(false)} />}
      <aside className={`${styles["cart-panel"]} ${cartOpen ? styles["cart-open"] : ""}`} aria-hidden={!cartOpen}>
        <div className={styles["cart-head"]}>
          <div><span className={styles["cart-kicker"]}>RETAIL CHECKOUT</span><h3>Customer Order</h3></div>
          <button className={styles["cart-close-btn"]} onClick={() => setCartOpen(false)}><X size={18}/></button>
          <button className={styles["clear-btn"]} onClick={() => setCart([])}><Trash2 size={14} /></button>
        </div>
        <div className={styles["cart-scroll"]}>
          {cart.map(item => (
            <div key={item.id} className={styles["cart-row"]}>
              <div className={styles["row-main"]}>
                <div className={styles["row-text"]}>
                  <strong>{item.name}</strong>
                  <small>{item.qty} x ₦{Number(item.price).toLocaleString()}</small>
                </div>
                <div className={styles["row-total"]}>₦{(item.price * item.qty).toLocaleString()}</div>
              </div>
            </div>
          ))}
          {cart.length === 0 && 
         <div className={styles["empty-state"]}>
        <Inbox size={48} color="#caa45f" /> {/* Brand gold looks better here */}
        <h4>Your cart is empty</h4><p>Add products from the inventory to get started.</p>
      </div>
          
          }
        </div>
        <div className={styles["cart-summary-box"]}>
          <div className={styles["total-payable-box"]}>
             <span className={styles["total-label"]}>Total Payable</span>
             <h2 className={styles["total-value"]}>₦{grandTotal.toLocaleString()}</h2>
          </div>
          <button className={styles["pay-button"]} disabled={cart.length === 0} onClick={() => setShowCheckout(true)}>Continue to Payment <ArrowRight size={18} /></button>
        </div>
      </aside>

      {/* CHECKOUT MODAL */}
      {showCheckout && (
        <div className={styles["modal-fixed-overlay"]}>
          <div className={styles["checkout-premium-modal"]}>
            <div className={styles["modal-header-premium"]}>
              <div className={styles["header-icon-wrap"]}><CreditCard size={20} color="#caa45f" /></div>
              <div className={styles["header-text-wrap"]}>
                <h3>Complete Product Sale</h3>
                <p>Review the order and choose how the customer paid</p>
              </div>
              <button className={styles["close-circle-btn"]} onClick={() => setShowCheckout(false)}><X size={18} /></button>
            </div>
            <div className={styles["checkout-content-grid"]}>
              <div className={styles["payment-selection-area"]}>
                <label className={styles["section-tag"]}>SELECT METHOD</label>
                <div className={styles["method-grid"]}>
                  <button className={`${styles["method-tile"]} ${paymentMethod === 'Cash' ? styles.methodActive : ""}`} onClick={() => setPaymentMethod('Cash')}>
                    <Banknote size={24} /> <span>Cash</span>
                  </button>
                  <button className={`${styles["method-tile"]} ${paymentMethod === 'POS' ? styles.methodActive : ""}`} onClick={() => setPaymentMethod('POS')}>
                    <CreditCard size={24} /> <span>POS / Card</span>
                  </button>
                  <button className={`${styles["method-tile"]} ${paymentMethod === 'Transfer' ? styles.methodActive : ""}`} onClick={() => setPaymentMethod('Transfer')}>
                    <Landmark size={24} /> <span>Transfer</span>
                  </button>
                </div>
                {paymentMethod === 'Cash' && (
                  <div className={styles["cash-logic-box"]}>
                    <label>CASH TENDERED</label>
                    <div className={styles["cash-input-wrap"]}>
                      <span className={styles["gold-naira"]}>₦</span>
                      <input type="number" value={amountReceived} onChange={(e) => setAmountReceived(e.target.value)} placeholder="0.00" />
                      <button className={styles["clear-input-btn"]} onClick={() => setAmountReceived("")}><X size={14} /></button>
                    </div>
                    <div className={styles["quick-tender-grid"]}>
                      {[1000, 2000, 5000, 10000, 20000].map((amt) => (
                        <button key={amt} className={styles["tender-btn"]} onClick={() => setAmountReceived(amt.toString())}>+ ₦{amt >= 1000 ? `${amt/1000}k` : amt}</button>
                      ))}
                    </div>
                    {changeDue > 0 && (
                      <div className={styles["change-bubble-premium"]}>
                        <div className={styles["change-label"]}>CUSTOMER CHANGE</div>
                        <div className={styles["change-value"]}>₦{changeDue.toLocaleString()}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className={styles["billing-summary-panel"]}>
                <label className={styles["section-tag"]}>BILLING SUMMARY</label>
                <div className={styles["summary-card-inner"]}>
                  <div className={styles["bill-total-row"]}>
                    <p>GRAND TOTAL</p>
                    <h2>₦{grandTotal.toLocaleString()}</h2>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles["modal-footer-actions"]}>
              <button className={styles["cancel-outline-btn"]} onClick={() => setShowCheckout(false)}>Back to Cart</button>
              <button className={styles["finish-gold-btn"]} onClick={handleFinishSale}>
                <Printer size={18} /> Complete Sale & Print
              </button>
            </div>
          </div>
        </div>
      )}

      {/* COMBINED ADD / EDIT MODAL */}
      {showAddModal && (
        <div className={styles["modal-fixed-overlay"]}>
          <div className={styles["admin-modal-container"]}>
            <div className={styles["modal-header-premium"]}>
              <div className={styles["header-icon-wrap"]}><Package size={20} color="#caa45f" /></div>
              <div className={styles["header-text-wrap"]}>
                <h3>{isEditing ? "Edit Product Details" : "Add New Product"}</h3>
                <p>{isEditing ? `Updating ${productForm.name}` : "Add a retail product and set its opening stock"}</p>
              </div>
              <button className={styles["close-circle-btn"]} onClick={closeModal}><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveProduct} className={styles["premium-admin-form"]}>
              <div className={styles["form-scroll-area"]}>
                <div className={styles["input-field-group"]}>
                  <label>PRODUCT NAME</label>
                  <div className={styles["input-with-icon"]}>
                    <Package size={16} className={styles["field-icon"]} />
                    <input type="text" required value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} placeholder="e.g. Deluxe Beard Balm" />
                  </div>
                </div>

                <div className={styles["form-grid-2col"]}>
                  <div className={styles["input-field-group"]}>
                    <label>UNIT PRICE (₦)</label>
                    <div className={styles["input-with-icon"]}>
                      <span className={styles["currency-symbol"]}>₦</span>
                      <input type="number" required value={productForm.price} onChange={e => setProductForm({...productForm, price: e.target.value})} placeholder="0.00" />
                    </div>
                  </div>
                  <div className={styles["input-field-group"]}>
                    <label>{isEditing ? "ADJUST STOCK" : "OPENING STOCK"}</label>
                    <div className={styles["input-with-icon"]}>
                      <TrendingUp size={16} className={styles["field-icon"]} />
                      <input type="number" required value={productForm.stock} onChange={e => setProductForm({...productForm, stock: e.target.value})} placeholder="Qty" />
                    </div>
                  </div>
                </div>

                <div className={styles["input-field-group"]}>
                  <label>INVENTORY CATEGORY</label>
                  <div className={styles["input-with-icon"]}>
                    <Search size={16} className={styles["field-icon"]} />
                    <select value={productForm.category} onChange={e => setProductForm({...productForm, category: e.target.value})}>
                      {categories.filter(c => c !== "All").map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className={styles["modal-footer-actions"]}>
                <button type="button" className={styles["cancel-outline-btn"]} onClick={closeModal}>Discard</button>
                <button type="submit" className={styles["save-gold-btn"]}>
                    {isEditing ? "Save Changes" : "Confirm & Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* HIDDEN THERMAL RECEIPT */}
      <div id="thermal-receipt" className={styles["thermal-receipt-container"]}>
        <div className={styles["receipt-header"]}>
          <h1 className={styles["receipt-logo"]}>DEYFRESH</h1>
          <p>Premium Grooming & Fragrance</p>
          <small>Shop H9, Ojaja Mall, Ogombo Rd, Ajah</small>
          <small>Tel: 0916 538 7780</small>
        </div>
        <div className={styles["receipt-divider"]}></div>
        <div className={styles["receipt-info-meta"]}>
          <p>Receipt: #{Date.now().toString().slice(-6)}</p>
          <p>Date: {new Date().toLocaleDateString()}</p>
          <p>Time: {new Date().toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</p>
          <p>Cashier: {user?.name || "Admin"}</p>
          <p>Payment: {paymentMethod}</p>
        </div>
        <div className={styles["receipt-divider"]}></div>
        <div className={styles["receipt-main-table"]}>
          <div className={`${styles["receipt-row"]} ${styles["receipt-header-row"]}`}>
            <span className={styles.colItem}>ITEM</span>
            <span className={styles.colQty}>QTY</span>
            <span className={styles.colPrice}>PRICE</span>
            <span className={styles.colTotal}>TOTAL</span>
          </div>
          {cart.map((item, idx) => (
            <div key={idx} className={styles["receipt-row"]}>
              <span className={styles.colItem}>{item.name}</span>
              <span className={styles.colQty}>{item.qty}</span>
              <span className={styles.colPrice}>₦{Number(item.price).toLocaleString()}</span>
              <span className={styles.colTotal}>₦{Number(item.price * item.qty).toLocaleString()}</span>
            </div>
          ))}
        </div>
        <div className={styles["receipt-divider"]}></div>
        <div className={styles["receipt-summary-area"]}>
          <div className={styles["grand-total-line"]}>
            <span>TOTAL</span>
            <span>₦{grandTotal.toLocaleString()}</span>
          </div>
        </div>
        <div className={styles["receipt-divider"]}></div>
        <div className={styles["receipt-footer-text"]}>
          <p>Thank you for choosing DeyFresh</p>
          <p>Goods sold are not returnable after 24 hours</p>
          <p>Powered by DeyFresh POS</p>
          <div className={styles["barcode-graphic"]}>|||||||||||||||||||||||||||||||||||</div>
        </div>
      </div>
    </div>
  );
}